<script setup lang="ts">
import { ref, computed } from 'vue';
import {
  equipArtifact, unequipSlot, repairArtifact, upgradeArtifact, fetchInventory,
  type Artifact, type EquipSlot, type Inventory,
} from '../game/inventory-api';
import { quickSellArtifact } from '../game/market-api';
import { itemArt } from '../game/art';
import { toast } from '../game/toast';
import { confirmAction } from '../game/confirm';
import cashIcon from '../assets/fimbulwake/icons/cash.webp';
import runesIcon from '../assets/fimbulwake/icons/runes.webp';
import {
  RARITY_HEX, SLOT_LABELS, STAT_META, UPGRADE_COST, MAX_ARTIFACT_LEVEL, effectiveLevel,
  scaledStat, scaledStats, compareStats, needsRepair, durabilityPct, repairCostPreview,
  quickSellPreview,
} from '../game/item-math';
import { t } from '../i18n';

/**
 * Self-contained item detail modal — performs its own gear mutations (equip /
 * unequip / upgrade / repair / quick-sell) and emits the fresh Inventory so any
 * parent (the Sea-Chest grid, the hero-card equipped strip, or the Dossier
 * paperdoll) can share ONE instance without owning the API calls itself.
 *
 * CATALOG MODE (`mode="catalog"`) — used by Market.vue's Tradestead to preview
 * NPC catalog / Vault gear you don't yet own. Hides all owned-only mutations
 * (equip/unequip/upgrade/repair/sell) and swaps in a single Buy action; the
 * caller supplies a synthetic Artifact (real id from the catalog/vault entry)
 * so the stat/compare-vs-equipped section above still works unchanged.
 */
const props = withDefaults(defineProps<{
  artifact: Artifact;
  slot: EquipSlot;
  isEquipped: boolean;
  /** Whatever currently occupies this slot (may equal `artifact` when isEquipped). */
  equippedInSlot: Artifact | null;
  mode?: 'owned' | 'catalog';
  /** Preformatted price string for the Buy action, e.g. "1,240 silver". */
  priceLabel?: string;
  buyBusy?: boolean;
  /** Disables + relabels the Buy button (e.g. "Not enough funds", "Sea-Chest full"). */
  blockedReason?: string | null;
}>(), { mode: 'owned', priceLabel: '', buyBusy: false, blockedReason: null });
const emit = defineEmits<{ (e: 'updated', inv: Inventory): void; (e: 'buy'): void; (e: 'close'): void }>();

const busy = ref(false);
const error = ref('');

const rarHex = computed(() => RARITY_HEX[props.artifact.rarity]);
const stats = computed(() => scaledStats(props.artifact));
/**
 * Deltas are only worth a column when there is something to compare AGAINST.
 * With an empty slot every `delta` equals its own `candidate`, which rendered
 * as two columns of identical numbers — the "duplicate stat columns" QA
 * 86cb1qvpt flagged. Empty slot → plain stat list.
 */
const comparing = computed(() => !props.isEquipped && props.equippedInSlot !== null);
const compareRows = computed(() =>
  comparing.value ? compareStats(props.artifact, props.equippedInSlot) : []);
const canUpgrade = computed(() => props.artifact.level < MAX_ARTIFACT_LEVEL);
const upgradeCost = computed(() => UPGRADE_COST[props.artifact.level] ?? 0);
const upgradePreview = computed(() => {
  // Sharpening moves the EFFECTIVE level by one, and the effective level is the
  // item's grade plus its track — so the preview reads off effectiveLevel, not
  // off `level`, or a tier-9 drop would show the numbers of a tier-1 piece.
  const now = effectiveLevel(props.artifact);
  const next = now + 1;
  const keys: ('attack' | 'defense' | 'magic' | 'speed' | 'hp')[] = ['attack', 'defense', 'magic', 'speed', 'hp'];
  return keys
    .map((k) => ({
      key: k,
      label: k === 'hp' ? t('items.stats.health') : STAT_META[k].label,
      before: scaledStat(props.artifact[k], now),
      after: scaledStat(props.artifact[k], next),
    }))
    .filter((r) => r.before !== 0 || r.after !== 0);
});
const repairCost = computed(() => repairCostPreview(props.artifact));
const showRepair = computed(() => needsRepair(props.artifact));

async function run(fn: () => Promise<Inventory>, done?: string) {
  busy.value = true; error.value = '';
  try {
    const inv = await fn();
    if (done) toast(done, { icon: itemArt(props.artifact.type, props.artifact.rarity, props.artifact.name), accent: rarHex.value });
    emit('updated', inv);
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = false; }
}

function onEquip() { void run(() => equipArtifact(props.artifact.id), t('items.modal.equipped', { name: props.artifact.name })); }
function onUnequip() { void run(() => unequipSlot(props.slot), t('items.modal.unequipped', { name: props.artifact.name })); }
async function onRepair() {
  const ok = await confirmAction({
    kicker: t('items.modal.forge'),
    title: t('items.modal.repairTitle', { name: props.artifact.name }),
    icon: itemArt(props.artifact.type, props.artifact.rarity, props.artifact.name),
    rows: [{ label: t('items.modal.price'), value: repairCost.value.toLocaleString(), icon: cashIcon }],
    confirmLabel: t('items.modal.repair'),
  });
  if (!ok) return;
  void run(async () => (await repairArtifact(props.artifact.id)).inventory, t('items.modal.repaired', { name: props.artifact.name }));
}
async function onUpgrade() {
  const ok = await confirmAction({
    kicker: t('items.modal.forge'),
    title: t('items.modal.upgradeTitle', { name: props.artifact.name }),
    icon: itemArt(props.artifact.type, props.artifact.rarity, props.artifact.name),
    rows: [
      // stated as a track (1 / 5 → 2 / 5), never as a bare number: a bare "level"
      // next to a tier-9 item is the ambiguity the split exists to remove
      { label: t('items.modal.level'),
        value: `${props.artifact.level} / ${MAX_ARTIFACT_LEVEL} → ${props.artifact.level + 1} / ${MAX_ARTIFACT_LEVEL}`,
        tone: 'dim' },
      { label: t('items.modal.price'), value: upgradeCost.value.toLocaleString(), icon: cashIcon },
    ],
    confirmLabel: t('items.modal.upgradeAction', { cost: upgradeCost.value.toLocaleString() }),
  });
  if (!ok) return;
  void run(async () => (await upgradeArtifact(props.artifact.id)).inventory, t('items.modal.upgraded', { name: props.artifact.name, level: props.artifact.level + 1, max: MAX_ARTIFACT_LEVEL }));
}

/** Mirrors MarketService.quickSell, in the item's own currency (see quickSellPreview).
 *  Preview only — the server's number is what actually pays. */
const sellPreview = computed(() => quickSellPreview(props.artifact));
const sellPayoutLabel = computed(() => (sellPreview.value.currency === 'PREMIUM'
  ? t('items.modal.runeAmount', { amount: sellPreview.value.payout.toLocaleString() })
  : t('items.modal.silverAmount', { amount: sellPreview.value.payout.toLocaleString() })));

async function onSell() {
  const ok = await confirmAction({
    kicker: t('items.modal.quickSell'),
    title: t('items.modal.sellTitle', { name: props.artifact.name }),
    icon: itemArt(props.artifact.type, props.artifact.rarity, props.artifact.name),
    note: t('items.modal.sellNote'),
    rows: [{
      label: t('items.modal.payout'),
      value: sellPayoutLabel.value,
      icon: sellPreview.value.currency === 'PREMIUM' ? runesIcon : cashIcon,
      tone: 'bright',
    }],
    confirmLabel: t('items.modal.sellIt'),
    danger: true,
  });
  if (!ok) return;
  busy.value = true; error.value = '';
  try {
    const res = await quickSellArtifact(props.artifact.id);
    toast(t('items.modal.sold', { name: props.artifact.name, amount: res.value.toLocaleString() }),
      { icon: itemArt(props.artifact.type, props.artifact.rarity, props.artifact.name), accent: rarHex.value });
    // the sold item is gone NOW — hand the fresh bag to the parent like every
    // other action does, or the sea-chest keeps showing it until a page reload
    try { emit('updated', await fetchInventory()); } catch { /* parent refetches on next nav */ }
    emit('close');
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = false; }
}
</script>

<template>
  <div class="fixed inset-0 z-[200] grid place-items-center p-4" style="background: rgba(6,4,12,0.85)" @click.self="emit('close')">
    <!-- THE TABLET — the item presented the way the relic cards promise it:
         the thing itself BIG and floating, its rarity dyed into a cord across
         the tablet's brow, everything else carved beneath it. -->
    <div class="im-frame relative w-full max-w-[720px] max-h-[88vh] overflow-y-auto bound-frame"
         :style="{ '--rar': rarHex, boxShadow: `0 10px 40px rgba(0,0,0,0.65), 0 0 26px color-mix(in srgb, ${rarHex} 22%, transparent)` }">

      <button class="btn-forge btn-forge--carved btn-forge-icon absolute top-3 right-3 z-10" aria-label="Close" @click="emit('close')">&times;</button>
      <span class="im-cord" aria-hidden="true"></span>

      <!-- The tablet reads across, not down: the thing itself holds the left
           half at the size it deserves, everything you decide with sits on the
           right, and the actions run the full width beneath both. -->
      <div class="im-body">
        <!-- LEFT — the item -->
        <div class="im-plate">
          <img :src="itemArt(artifact.type, artifact.rarity, artifact.name)" :alt="artifact.type" class="im-art" />
          <div class="flex items-center justify-center gap-2 flex-wrap">
            <h2 class="font-heading leading-none im-name" :style="{ color: rarHex }">{{ artifact.name }}</h2>
            <span v-if="isEquipped" class="im-tag leading-none" style="--rar:#c8a24c">{{ t('items.modal.worn') }}</span>
          </div>
          <div class="mt-1.5 flex items-center justify-center gap-2 font-label uppercase text-[11px] im-sub flex-wrap">
            <span>{{ SLOT_LABELS[slot] }}</span>
            <span class="opacity-50">᛭</span>
            <span :style="{ color: rarHex }">{{ artifact.rarity.toLowerCase() }}</span>
            <span class="opacity-50">᛭</span>
            <span>{{ t('items.modal.tier', { tier: effectiveLevel(artifact) }) }}</span>
          </div>
          <p v-if="artifact.description" class="font-body im-desc mt-2 leading-snug">{{ artifact.description }}</p>
          <div v-if="artifact.setName" class="mt-1.5 font-body text-xs text-gold capitalize">{{ t('items.modal.set', { name: artifact.setName }) }}</div>
          <div v-if="artifact.statusEffect" class="mt-0.5 font-body text-xs text-purple-light">{{ artifact.statusEffect }}</div>
        </div>

        <!-- RIGHT — what you decide with -->
        <div class="im-facts">
          <p v-if="error" class="mb-3 text-sm text-error">{{ error }}</p>

          <!-- stats — deltas only when something is actually equipped to beat -->
          <div class="im-h mb-2">{{ isEquipped ? t('items.modal.contributing') : comparing ? t('items.modal.vsEquipped') : t('items.modal.statsLabel') }}</div>
          <div class="flex flex-col gap-1.5 mb-4">
            <template v-if="comparing">
              <div v-for="r in compareRows" :key="r.key" class="flex items-center justify-between gap-2">
                <span class="font-label uppercase tracking-wider text-[11px] text-text-dim shrink-0 leading-none">{{ r.label }}</span>
                <span class="font-body tabular-nums text-text flex-1 text-right leading-none">{{ r.candidate }}</span>
                <span class="font-body tabular-nums text-xs w-12 text-right leading-none"
                      :class="r.delta > 0 ? 'text-success' : r.delta < 0 ? 'text-error' : 'text-text-dim'">
                  {{ r.delta > 0 ? '+' : '' }}{{ r.delta }}
                </span>
              </div>
            </template>
            <template v-else>
              <div v-for="(v, k) in stats" :key="k" v-show="v !== 0" class="flex items-center justify-between gap-2">
                <span class="font-label uppercase tracking-wider text-[11px] text-text-dim leading-none">{{ k === 'hp' ? t('items.stats.health') : STAT_META[k as 'attack'].label }}</span>
                <span class="font-body tabular-nums text-gold leading-none">+{{ v }}</span>
              </div>
            </template>
          </div>

          <!-- durability (owned items only) -->
          <template v-if="mode === 'owned' && artifact.isBreakable && artifact.maxDurability != null">
            <div class="im-h mb-1.5">{{ t('items.modal.durability') }}</div>
            <div class="wear-track mb-3">
              <div class="wear-fill" :style="{ width: durabilityPct(artifact) + '%', background: durabilityPct(artifact) > 50 ? 'var(--color-success)' : durabilityPct(artifact) > 25 ? '#d9a441' : 'var(--color-error)' }"></div>
            </div>
          </template>

          <!-- upgrade preview (owned items only) -->
          <template v-if="mode === 'owned'">
            <template v-if="canUpgrade">
              <div class="im-h mb-1.5">{{ t('items.modal.upgrade', { from: artifact.level, to: artifact.level + 1, max: MAX_ARTIFACT_LEVEL }) }}</div>
              <div class="flex flex-col gap-1 mb-3 border border-gold/25 bg-gold/5 p-2.5">
                <div v-for="r in upgradePreview" :key="r.key" class="flex items-center justify-between text-xs">
                  <span class="font-label uppercase tracking-wider text-text-dim leading-none">{{ r.label }}</span>
                  <span class="font-body tabular-nums text-text leading-none">{{ r.before }} <span class="text-gold">→ {{ r.after }}</span></span>
                </div>
              </div>
            </template>
            <p v-else class="font-body text-xs text-text-dim mb-3 italic">{{ t('items.modal.maxLevel', { max: MAX_ARTIFACT_LEVEL }) }}</p>
          </template>
        </div>
      </div>

      <div class="im-foot">
        <span class="im-rule" aria-hidden="true"></span>

        <!-- actions: owned-item mutations -->
        <div v-if="mode === 'owned'" class="flex flex-wrap gap-2">
          <button v-if="!isEquipped" class="btn-forge btn-forge--rune btn-forge--sm" :disabled="busy" @click="onEquip">{{ busy ? '…' : t('items.modal.equip') }}</button>
          <button v-else class="btn-forge btn-forge--carved btn-forge--sm" :disabled="busy" @click="onUnequip">{{ busy ? '…' : t('items.modal.unequip') }}</button>

          <button v-if="canUpgrade" class="btn-forge btn-forge--sm" :disabled="busy" @click="onUpgrade">
            {{ busy ? '…' : t('items.modal.upgradeAction', { cost: upgradeCost }) }}
          </button>

          <button v-if="showRepair" class="btn-forge btn-forge--sm" :disabled="busy" @click="onRepair">
            {{ busy ? '…' : t('items.modal.repairAction', { cost: repairCost }) }}
          </button>

          <button v-if="!isEquipped" class="btn-forge btn-forge--blood btn-forge--sm ml-auto" :disabled="busy" @click="onSell">
            {{ busy ? '…' : t('items.modal.sell') }}
          </button>
        </div>

        <!-- actions: catalog / vault preview — single Buy action -->
        <div v-else class="flex">
          <button
            class="btn-forge btn-forge--rune w-full"
            :disabled="buyBusy || !!blockedReason"
            @click="emit('buy')"
          >{{ buyBusy ? '…' : (blockedReason ?? t('items.modal.buy', { price: priceLabel })) }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* the tablet face — granite over the sunken gradient, with the rarity's
   breath glowing up behind the brow where the item floats */
.im-frame {
  background:
    radial-gradient(80% 34% at 50% 0%, color-mix(in srgb, var(--rar, #9db0c6) 14%, transparent), transparent 70%),
    var(--tex-stone),
    linear-gradient(180deg, #131a26, #0c1119 58%, #0e141d);
  background-size: auto, 150px 150px, auto;
  background-blend-mode: normal, soft-light, normal;
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--color-ice-dim) 35%, transparent) transparent;
}

/* ── the cord, chased across the tablet's brow ──────────────────────────── */
.im-cord {
  position: absolute; top: 9px; left: 16px; right: 16px; height: 7px;
  background: var(--rar, #9db0c6);
  -webkit-mask: var(--orn-cord-dark) repeat-x left center / auto 7px;
  mask: var(--orn-cord-dark) repeat-x left center / auto 7px;
  opacity: 0.9;
  pointer-events: none;
}

/* ── the two halves ──────────────────────────────────────────────────────
   Portrait, the item stacked above a single narrow stat column left the
   tablet mostly empty and forced the numbers into a thin strip. Landscape
   from 560px up: the item takes the left plate at a size worth looking at,
   the numbers take the right. Below that it stacks, which is the right
   answer on a phone. */
.im-body {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  padding: 26px 20px 4px;
}
@media (min-width: 560px) {
  .im-body {
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
    gap: 22px;
    align-items: start;
  }
  /* the seam between the halves — a carved keyline, not a border box.
     padding-top clears the close button, which shares this band. */
  .im-facts {
    padding-left: 22px;
    padding-top: 16px;
    border-left: 1px solid rgba(226, 236, 248, 0.08);
    box-shadow: inset 1px 0 0 rgba(0, 0, 0, 0.5);
  }
}
.im-plate { text-align: center; }
.im-art {
  width: 100%;
  max-width: 190px;
  aspect-ratio: 1;
  object-fit: contain;
  margin: 0 auto 10px;
  filter: drop-shadow(0 12px 16px rgba(0, 0, 0, 0.75));
}
.im-name { font-size: clamp(19px, 3.4vw, 25px); text-shadow: 0 2px 6px rgba(0, 0, 0, 0.9); }
.im-sub { letter-spacing: 0.07em; color: #9db0c6; }
.im-desc { font-style: italic; font-size: 12px; color: #9db0c6; max-width: 40ch; margin-inline: auto; }

.im-foot { padding: 0 20px 20px; }
.im-rule {
  display: block; width: 190px; max-width: 70%; height: 9px; margin: 4px auto 14px;
  background: var(--orn-divider-gold) no-repeat center / contain;
}

.im-h {
  font-family: var(--font-label, sans-serif);
  text-transform: uppercase; letter-spacing: 0.08em;
  font-size: 11px; color: #9db0c6;
}
.im-tag {
  --rar: #9a927f;
  font-family: var(--font-label, sans-serif);
  font-size: 9px; text-transform: uppercase; letter-spacing: 0.07em;
  padding: 2px 7px; border-radius: 0;
  color: var(--rar);
  border: 1px solid color-mix(in srgb, var(--rar) 55%, transparent);
  background: color-mix(in srgb, var(--rar) 15%, transparent);
}
.wear-track { height: 6px; border-radius: 0; background: rgba(0,0,0,0.5); border: 1px solid rgba(0,0,0,0.4); overflow: hidden; }
.wear-fill { height: 100%; border-radius: 0; transition: width 400ms ease-out; }
</style>
